/* istanbul ignore file - better tested visually */

import { getModelSceneProperties } from '@partful/features/instructions/cad/lib/getModelSceneProperties';
import { classNames } from '@partful/shared/lib/classNames';
import { AdaptiveDpr, Environment, OrbitControls, Stats } from '@react-three/drei';
import { Canvas, CanvasProps, GroupProps } from '@react-three/fiber';
import { useEffect, useMemo } from 'react';
import hdri from 'src/assets/hdri/studio_small_08_1k.hdr';
import { Matrix4, Mesh, MeshStandardMaterial, Object3D, Vector3 } from 'three';
import styles from './ModelViewer.module.scss';

export type ModelViewerProps = {
  model: Object3D;
  cloneModel?: boolean;
  swapYZAxis?: boolean;
  swapFacing?: boolean;
  normalizeMaterials?: boolean;
  greyscale?: boolean;
  modelProps?: GroupProps;
} & Omit<CanvasProps, 'children'> &
  Partial<Pick<CanvasProps, 'children'>>;

export function ModelViewer({
  model: originalModel,
  cloneModel = false,
  swapYZAxis = false,
  swapFacing = false,
  normalizeMaterials = false,
  greyscale = false,
  children,
  modelProps,
  className,
  ...rest
}: ModelViewerProps) {
  const model = useMemo(() => (cloneModel ? originalModel.clone() : originalModel), [cloneModel, originalModel]);
  const { scale } = useMemo(() => getModelSceneProperties(model), [model]);

  useEffect(() => {
    model.traverse((object) => {
      if (object instanceof Mesh && object.material instanceof MeshStandardMaterial) {
        if (object.userData.originalMaterial == null) {
          object.userData.originalMaterial = object.material.clone();
        }

        const newMat = object.userData.originalMaterial.clone();

        if (normalizeMaterials && object.userData.originalMaterial.metalness === 1) {
          newMat.metalness = 0.1;
        }

        if (greyscale) {
          newMat.color.set(0xb8b8b8);
        }

        object.material = newMat;
      }
    });
  }, [greyscale, normalizeMaterials, model]);

  // Calculate coordinate system matrix
  const coordinateSystemMatrix = useMemo(() => {
    const scaledIdentityMatrix = new Matrix4(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1 / scale).multiplyScalar(
      scale
    );

    const swapYZMatrix = new Matrix4(1, 0, 0, 0, 0, 0, 1, 0, 0, -1, 0, 0, 0, 0, 0, 1);
    const swapFacingMatrix = new Matrix4(-1, 0, 0, 0, 0, 1, 0, 0, 0, 0, -1, 0, 0, 0, 0, 1);

    let finalTransformMatrix = scaledIdentityMatrix;

    if (swapFacing) {
      finalTransformMatrix = finalTransformMatrix.multiply(swapFacingMatrix);
    }

    if (swapYZAxis) {
      finalTransformMatrix = finalTransformMatrix.multiply(swapYZMatrix);
    }

    return finalTransformMatrix;
  }, [scale, swapFacing, swapYZAxis]);

  // Calculate the default camera positioning and focus
  const defaultCameraPosition = useMemo(() => new Vector3(-2, 1, 4).normalize().multiplyScalar(20), []);

  return (
    <Canvas
      camera={{ position: defaultCameraPosition.toArray(), fov: 45 }}
      frameloop="demand"
      className={classNames(styles.modelViewer, className)}
      data-testid="model-viewer-canvas"
      {...rest}
    >
      {/* Reduces canvas dpr when perfomrance is suffering */}
      <AdaptiveDpr />
      <group name="lighting">
        <ambientLight intensity={1} />
        <directionalLight
          position={[-0.021620146930217743, 1.4572889804840088, 2.5636305809020996]}
          rotation={[-0.9306818754685443, -0.38385020191945607, -0.46593567629603494]}
          intensity={0.2}
        />
        <directionalLight
          position={[-1.4427666664123535, 1.4572889804840088, -3.6674907207489014]}
          rotation={[-2.8691385788104666, -0.4717099967963045, -2.83753080693173]}
          intensity={0.2}
        />
        <directionalLight
          position={[1.2379957437515259, 1.4572889804840088, -3.6674907207489014]}
          rotation={[-2.9280135086150874, 0.03508449215431484, 2.7606639469238328]}
          intensity={0.2}
        />
        <Environment files={hdri} environmentIntensity={normalizeMaterials ? 0.5 : 1} />
      </group>
      <group name="model" matrix={coordinateSystemMatrix} matrixAutoUpdate={false} {...modelProps}>
        <primitive object={model} />
      </group>
      <OrbitControls makeDefault maxDistance={100} minDistance={1} enableDamping dampingFactor={0.2} />
      {children}
      {process.env.REACT_APP_DEV_SCENE_STATS_ENABLED === 'true' && <Stats />}
    </Canvas>
  );
}
